import { Notification } from "@bbb/model";
import { nowEpochMs, toEpochMs } from "@bbb/utils";

export type NotificationCategory =
  | "quests"
  | "rewards"
  | "campaigns"
  | "verification"
  | "system";

export type NotificationCategoryTab = "all" | NotificationCategory;

export type NotificationGroup = {
  category: NotificationCategory;
  label: string;
  notifications: Notification[];
  unreadCount: number;
  latestTimestampMs: number;
};

export const notificationCategoryOrder: NotificationCategory[] = [
  "quests",
  "rewards",
  "campaigns",
  "verification",
  "system",
];

const notificationCategoryLabels: Record<NotificationCategoryTab, string> = {
  all: "All",
  quests: "Quest updates",
  rewards: "Rewards",
  campaigns: "Campaign activity",
  verification: "Verification",
  system: "System",
};

const notificationCategorySingularLabels: Record<NotificationCategory, string> =
  {
    quests: "quest update",
    rewards: "reward",
    campaigns: "campaign update",
    verification: "verification update",
    system: "system update",
  };

const notificationCategoryPluralLabels: Record<NotificationCategory, string> = {
  quests: "quest updates",
  rewards: "rewards",
  campaigns: "campaign updates",
  verification: "verification updates",
  system: "system updates",
};

export function getNotificationCategory(
  type: Notification["type"],
): NotificationCategory {
  switch (type) {
    case "feedback":
    case "quest-retry":
    case "quest-deadline":
    case "quest-activity":
    case "new-quest":
      return "quests";
    case "achievement":
    case "badge-upgrade":
    case "reward":
    case "block-earned":
    case "referral-milestone":
      return "rewards";
    case "campaign-live":
    case "campaign-ended":
    case "leaderboard-created":
      return "campaigns";
    case "kyc":
      return "verification";
    default:
      return "system";
  }
}

export function getNotificationCategoryLabel(
  category: NotificationCategoryTab,
) {
  return notificationCategoryLabels[category];
}

export function getNotificationCategoryCountLabel(
  category: NotificationCategory,
  count: number,
) {
  const label =
    count === 1
      ? notificationCategorySingularLabels[category]
      : notificationCategoryPluralLabels[category];

  return `${count} ${label}`;
}

export function getNotificationTimestampMs(notification: Notification) {
  if (!notification.timestamp) return 0;

  try {
    return toEpochMs(notification.timestamp as string | number | Date);
  } catch {
    return 0;
  }
}

export function sortNotificationsByNewest(
  notifications: Notification[],
): Notification[] {
  return [...notifications].sort(
    (left, right) =>
      getNotificationTimestampMs(right) - getNotificationTimestampMs(left),
  );
}

export function groupNotificationsByCategory(
  notifications: Notification[],
): NotificationGroup[] {
  const groups = new Map<NotificationCategory, Notification[]>();

  notifications.forEach((notification) => {
    const category = getNotificationCategory(notification.type);
    const group = groups.get(category) ?? [];
    group.push(notification);
    groups.set(category, group);
  });

  const groupedByCategory = notificationCategoryOrder
    .map((category) => {
      const groupedNotifications = groups.get(category);
      if (!groupedNotifications?.length) return null;

      const sortedNotifications = sortNotificationsByNewest(groupedNotifications);

      return {
        category,
        label: getNotificationCategoryLabel(category),
        notifications: sortedNotifications,
        unreadCount: sortedNotifications.filter((notification) => !notification.read)
          .length,
        latestTimestampMs: getNotificationTimestampMs(sortedNotifications[0]),
      };
    })
    .filter((group): group is NotificationGroup => Boolean(group));

  return groupedByCategory.sort((left, right) => {
    const timestampDiff = right.latestTimestampMs - left.latestTimestampMs;
    if (timestampDiff !== 0) return timestampDiff;

    return (
      notificationCategoryOrder.indexOf(left.category) -
      notificationCategoryOrder.indexOf(right.category)
    );
  });
}

export function buildNotificationPopupSummary(
  notifications: Notification[],
) {
  const groups = groupNotificationsByCategory(notifications);
  const count = notifications.length;

  if (count === 0 || groups.length === 0) {
    return {
      title: "No new notifications",
      description: undefined,
    };
  }

  if (groups.length === 1) {
    const group = groups[0];
    const latestNotification = group.notifications[0];

    return {
      title:
        count === 1
          ? latestNotification.title
          : getNotificationCategoryCountLabel(group.category, count),
      description:
        count === 1
          ? latestNotification.message
          : latestNotification.title,
    };
  }

  return {
    title: `${count} new notifications`,
    description: groups
      .map((group) =>
        getNotificationCategoryCountLabel(
          group.category,
          group.notifications.length,
        ),
      )
      .join(" - "),
  };
}

export function getNotificationTimeAgo(timestamp: Notification["timestamp"]) {
  if (!timestamp) return "Just now";

  let epochMs: number;
  try {
    epochMs = toEpochMs(timestamp as string | number | Date);
  } catch {
    return "Just now";
  }

  const diff = nowEpochMs() - epochMs;
  if (diff <= 0) return "Just now";

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "Just now";
}
